/* eslint-disable */
/* Disabling eslint for this file as it is a clientlib from Adobe */
/*******************************************************************************
 * Copyright 2018 Adobe
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ******************************************************************************/

import { scrollToAnchor } from 'js/ra.js';
import { gsap } from 'gsap';

(function () {

    const containerUtils = window.CQ && window.CQ.CoreComponents && window.CQ.CoreComponents.container && window.CQ.CoreComponents.container.utils ? window.CQ.CoreComponents.container.utils : undefined;

    const dataLayerEnabled = document.body.hasAttribute('data-cmp-data-layer-enabled');
    const dataLayer = (dataLayerEnabled) ? window.adobeDataLayer = window.adobeDataLayer || [] : undefined;

    const NS = 'cmp';
    const IS = 'tabs';

    const keyCodes = {
        END: 35,
        HOME: 36,
        ARROW_LEFT: 37,
        ARROW_UP: 38,
        ARROW_RIGHT: 39,
        ARROW_DOWN: 40
    };

    const selectors = {
        self: `[data-${NS}-is="${IS}"]`,
        active: {
            tab: 'cmp-tabs__tab--active',
            tabpanel: 'cmp-tabs__tabpanel--active',
            accbutton: 'active'
        }
    };

    /**
     * Tabs Configuration
     *
     * @typedef {Object} TabsConfig Represents a Tabs configuration
     * @property {HTMLElement} element The HTMLElement representing the Tabs
     * @property {Object} options The Tabs options
     */

    /**
     * Tabs
     *
     * @class Tabs
     * @classdesc An interactive Tabs component for navigating a list of tabs
     * @param {TabsConfig} config The Tabs configuration
     */
    function Tabs(config) {
        const that = this;

        if (config && config.element) {
            init(config);
        }

        /**
         * Initializes the Tabs
         *
         * @private
         * @param {TabsConfig} config The Tabs configuration
         */
        function init(config) {
            that._config = config;

            // prevents multiple initialization
            config.element.removeAttribute(`data-${NS}-is`);

            cacheElements(config.element);
            that._active = getActiveIndex(that._elements.tab);

            if (that._elements.tabpanel) {
                refreshActive();
                bindEvents();
            }

            if (containerUtils) {
                // Show the tab based on deep-link-id if it matches with any existing tab item id
                const deepLinkItemIdx = containerUtils.getDeepLinkItemIdx(that, 'tabpanel');
                if (deepLinkItemIdx) {
                    const deepLinkItem = that._elements.tab[deepLinkItemIdx];
                    if (deepLinkItem && that._elements.tab[that._active].id !== deepLinkItem.id) {
                        navigateAndFocusTab(deepLinkItemIdx);
                    }
                }
            }

            if (window.Granite && window.Granite.author && window.Granite.author.MessageChannel) {
                /*
                 * Editor message handling:
                 * - subscribe to "cmp.panelcontainer" message requests sent by the editor frame
                 * - check that the message data panel container type is correct and that the id (path) matches this specific Tabs component
                 * - if so, route the "navigate" operation to enact a navigation of the Tabs based on index data
                 */
                new window.Granite.author.MessageChannel('cqauthor', window).subscribeRequestMessage('cmp.panelcontainer', (message) => {
                    if (message.data && message.data.type === 'cmp-tabs' && message.data.id === that._elements.self.dataset.cmpPanelcontainerId) {
                        if (message.data.operation === 'navigate') {
                            navigate(message.data.index);
                        }
                    }
                });
            }
            readURLHash(config.element);
        }

        /**
         * Returns the index of the active tab, if no tab is active returns 0
         *
         * @param {Array} tabs Tab elements
         * @returns {Number} Index of the active tab, 0 if none is active
         */
        function getActiveIndex(tabs) {
            if (tabs) {
                for (let i = 0; i < tabs.length; i++) {
                    if (tabs[i].classList.contains(selectors.active.tab)) {
                        return i;
                    }
                }
            }
            return 0;
        }

        /**
         * Caches the Tabs elements as defined via the {@code data-tabs-hook="ELEMENT_NAME"} markup API
         *
         * @private
         * @param {HTMLElement} wrapper The Tabs wrapper element
         */
        function cacheElements(wrapper) {
            that._elements = {};
            that._elements.self = wrapper;
            const hooks = that._elements.self.querySelectorAll(`[data-${NS}-hook-${IS}]`);

            for (let i = 0; i < hooks.length; i++) {
                const hook = hooks[i];
                if (hook.closest(`.${NS}-${IS}`) === that._elements.self) { // only process own tab elements
                    let capitalized = IS;
                    capitalized = capitalized.charAt(0).toUpperCase() + capitalized.slice(1);
                    const key = hook.dataset[`${NS}Hook${capitalized}`];
                    if (that._elements[key]) {
                        if (!Array.isArray(that._elements[key])) {
                            const tmp = that._elements[key];
                            that._elements[key] = [tmp];
                        }
                        that._elements[key].push(hook);
                    } else {
                        that._elements[key] = hook;
                    }
                }
            }
        }

        /**
         * Binds Tabs event handling
         *
         * @private
         */
        function bindEvents() {
            const tabs = that._elements.tab;
            if (tabs) {
                for (var i = 0; i < tabs.length; i++) {
                    (function (index) {
                        tabs[i].addEventListener('click', (event) => {
                            navigateAndFocusTab(index);
                        });
                        tabs[i].addEventListener('keydown', (event) => {
                            onKeyDown(event);
                        });
                    }(i));
                }
            }
        }

        /**
         * Handles tab keydown events
         *
         * @private
         * @param {Object} event The keydown event
         */
        function onKeyDown(event) {
            const index = that._active;
            const lastIndex = that._elements.tab.length - 1;

            switch (event.keyCode) {
                case keyCodes.ARROW_LEFT:
                case keyCodes.ARROW_UP:
                    event.preventDefault();
                    if (index > 0) {
                        navigateAndFocusTab(index - 1);
                    }
                    break;
                case keyCodes.ARROW_RIGHT:
                case keyCodes.ARROW_DOWN:
                    event.preventDefault();
                    if (index < lastIndex) {
                        navigateAndFocusTab(index + 1);
                    }
                    break;
                case keyCodes.HOME:
                    event.preventDefault();
                    navigateAndFocusTab(0);
                    break;
                case keyCodes.END:
                    event.preventDefault();
                    navigateAndFocusTab(lastIndex);
                    break;
                default:

            }
        }

        /**
         * Refreshes the tab markup based on the current {@code Tabs#_active} index
         *
         * @private
         */
        function refreshActive() {
            const accbuttons = that._elements.accbutton;
            const tabpanels = that._elements.tabpanel;
            const tabs = that._elements.tab;

            if (tabpanels) {
                if (Array.isArray(tabpanels)) {
                    for (let i = 0; i < tabpanels.length; i++) {
                        if (i === parseInt(that._active)) {
                            accbuttons[i].classList.replace('closed', selectors.active.accbutton);
                            tabpanels[i].classList.add(selectors.active.tabpanel);
                            tabpanels[i].removeAttribute('aria-hidden');
                            tabs[i].classList.add(selectors.active.tab);
                            tabs[i].setAttribute('aria-selected', true);
                            tabs[i].setAttribute('tabindex', '0');

                            const carouselInTab = tabpanels[i].querySelector('.carousel');
                            if(carouselInTab != null) {
                                window.dispatchEvent(new CustomEvent('resize-carousel'));
                            }
                        } else {
                            accbuttons[i].classList.replace(selectors.active.accbutton, 'closed');
                            tabpanels[i].classList.remove(selectors.active.tabpanel);
                            tabpanels[i].setAttribute('aria-hidden', true);
                            tabs[i].classList.remove(selectors.active.tab);
                            tabs[i].setAttribute('aria-selected', false);
                            tabs[i].setAttribute('tabindex', '-1');
                        }
                    }
                } else {
                    // only one tab
                    accbuttons.classList.add(selectors.active.accbutton);
                    tabpanels.classList.add(selectors.active.tabpanel);
                    tabs.classList.add(selectors.active.tab);

                    const carouselInTab = tabpanels.querySelector('.carousel');
                    if(carouselInTab != null) {
                        window.dispatchEvent(new CustomEvent('resize-carousel'));
                    }
                }
            }
        }

        /**
         * Focuses the element and prevents scrolling the element into view
         *
         * @param {HTMLElement} element Element to focus
         */
        function focusWithoutScroll(element) {
            const x = window.scrollX;
            const y = window.scrollY;
            element.focus();
            window.scrollTo(x, y);
        }

        /**
         * Navigates to the tab at the provided index
         *
         * @private
         * @param {Number} index The index of the tab to navigate to
         */
        function navigate(index) {
            that._active = index;
            refreshActive();
            window.dispatchEvent(new CustomEvent('init-video-players')); // Trigger video initialization
            window.dispatchEvent(new Event('resize')); // Recalculate video height
        }

        /**
         * Navigates to the item at the provided index and ensures the active tab gains focus
         *
         * @private
         * @param {Number} index The index of the item to navigate to
         */
        function navigateAndFocusTab(index) {
            const exActive = that._active;
            navigate(index);
            focusWithoutScroll(that._elements.tab[index]);

            if (dataLayerEnabled) {

                const activeItem = getDataLayerId(that._elements.tabpanel[index].dataset.cmpDataLayer);
                const exActiveItem = getDataLayerId(that._elements.tabpanel[exActive].dataset.cmpDataLayer);

                dataLayer.push({
                    event: 'cmp:show',
                    eventInfo: {
                        path: `component.${activeItem}`
                    }
                });

                dataLayer.push({
                    event: 'cmp:hide',
                    eventInfo: {
                        path: `component.${exActiveItem}`
                    }
                });

                const tabsId = that._elements.self.id;
                const uploadPayload = { component: {} };
                uploadPayload.component[tabsId] = { shownItems: [activeItem] };

                const removePayload = { component: {} };
                removePayload.component[tabsId] = { shownItems: undefined };

                dataLayer.push(removePayload);
                dataLayer.push(uploadPayload);
            }

            updateURLHash(that._elements.tab[index].id);
        }

        function updateURLHash(id) {
            if (window.location.hash !== `tabid-${id}`) {
                window.location.hash = `tabid-${id}`;
            }
        }

        function readURLHash(element) {
            const hash = window.location.hash;
            if (hash) {
                const id = hash.split('tabid-')[1];
                const tab = element.querySelector(`#${id}`);
                if (tab) {
                    if (document.readyState === 'complete') {
                        tab.click();
                        scrollToAnchor(tab.closest('.cmp-tabs'));
                    } else {
                        window.addEventListener('load', function() {
                            tab.click();
                            scrollToAnchor(tab.closest('.cmp-tabs'));
                        });
                    }
                }
            }
        }
    }

    /**
     * Reads options data from the Tabs wrapper element, defined via {@code data-cmp-*} data attributes
     *
     * @private
     * @param {HTMLElement} element The Tabs element to read options data from
     * @returns {Object} The options read from the component data attributes
     */
    function readData(element) {
        const data = element.dataset;
        const options = [];
        let capitalized = IS;
        capitalized = capitalized.charAt(0).toUpperCase() + capitalized.slice(1);
        const reserved = ['is', `hook${capitalized}`];

        for (let key in data) {
            if (data.hasOwnProperty(key)) {
                const value = data[key];

                if (key.indexOf(NS) === 0) {
                    key = key.slice(NS.length);
                    key = key.charAt(0).toLowerCase() + key.substring(1);

                    if (reserved.indexOf(key) === -1) {
                        options[key] = value;
                    }
                }
            }
        }

        return options;
    }

    /**
     * Parses the dataLayer string and returns the ID
     *
     * @private
     * @param {String} componentDataLayer the dataLayer string
     * @returns {String} dataLayerId or undefined
     */
    function getDataLayerId(componentDataLayer) {
        return Object.keys(JSON.parse(componentDataLayer))[0];
    }

    /**
     * Document ready handler and DOM mutation observers. Initializes Tabs components as necessary.
     *
     * @private
     */
    function onDocumentReady() {
        const elements = document.querySelectorAll(selectors.self);
        for (let i = 0; i < elements.length; i++) {
            new Tabs({ element: elements[i], options: readData(elements[i]) });
        }

        const MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
        const body = document.querySelector('body');
        const observer = new MutationObserver(((mutations) => {
            mutations.forEach((mutation) => {
                // needed for IE
                const nodesArray = [].slice.call(mutation.addedNodes);
                if (nodesArray.length > 0) {
                    nodesArray.forEach((addedNode) => {
                        if (addedNode.querySelectorAll) {
                            const elementsArray = [].slice.call(addedNode.querySelectorAll(selectors.self));
                            elementsArray.forEach((element) => {
                                new Tabs({ element, options: readData(element) });
                            });
                        }
                    });
                }
            });
        }));

        observer.observe(body, {
            subtree: true,
            childList: true,
            characterData: true
        });
    }

    if (document.readyState !== 'loading') {
        onDocumentReady();
    } else {
        document.addEventListener('DOMContentLoaded', onDocumentReady);
    }

    // Helper function to replicate jQuery slideDown behavior with GSAP
    function slideDown(element, duration = 500, callback) {
        element.style.display = '';
        element.style.height = '0px';
        element.style.overflow = 'hidden';

        // Get natural height
        const naturalHeight = element.scrollHeight;

        gsap.to(element, {
            height: naturalHeight,
            duration: duration / 1000,
            ease: 'power2.inOut',
            onComplete: () => {
                element.style.height = '';
                element.style.overflow = '';
                if (callback) callback.call(element);
            }
        });
    }

    // Helper function to replicate jQuery slideUp behavior with GSAP
    function slideUp(element, duration = 500, callback) {
        element.style.overflow = 'hidden';

        gsap.to(element, {
            height: 0,
            duration: duration / 1000,
            ease: 'power2.inOut',
            onComplete: () => {
                element.style.display = 'none';
                element.style.height = '';
                element.style.overflow = '';
                if (callback) callback.call(element);
            }
        });
    }

    /* MOBILE (ACCORDION) - EXPAND ALL BUTTON */
    document.addEventListener('click', function(event) {
        if (event.target.matches('.accordion-button .expand-all')) {
            const expandButton = event.target;
            expandButton.classList.remove('active');
            const closeButton = expandButton.parentNode.querySelector('.close-all');
            closeButton.classList.add('active');

            const tabsComp = expandButton.closest('.cmp-tabs');
            const accButtons = tabsComp.querySelectorAll('.acc-button');
            const tabpanels = tabsComp.querySelectorAll('.cmp-tabs__tabpanel');

            accButtons.forEach(btn => {
                btn.classList.remove('closed');
                btn.classList.add('active');
            });

            tabpanels.forEach(panel => {
                slideDown(panel, 500, function() {
                    const tabRef = this.id;
                    const correspondingTab = document.querySelector(`#${tabRef}`);
                    const correspondingTabBtn = document.querySelector(`#${tabRef}-tab`);

                    if (correspondingTab) {
                        correspondingTab.classList.add('cmp-tabs__tabpanel--active');
                        correspondingTab.removeAttribute('aria-hidden');
                        correspondingTab.removeAttribute('style');
                    }

                    if (correspondingTabBtn) {
                        correspondingTabBtn.classList.add('cmp-tabs__tab--active');
                        correspondingTabBtn.setAttribute('aria-selected', 'true');
                        correspondingTabBtn.setAttribute('tabindex', '0');
                    }

                    this.classList.add('cmp-tabs__tabpanel--active');
                    this.removeAttribute('aria-hidden');
                    this.removeAttribute('style');
                });
            });

            const carouselInTabs = tabsComp.querySelectorAll('.carousel');
            if (carouselInTabs.length > 0) {
                window.dispatchEvent(new CustomEvent('resize-carousel'));
            }
        }
    });

    /* MOBILE (ACCORDION) - CLOSE ALL BUTTON */
    document.addEventListener('click', function(event) {
        if (event.target.matches('.accordion-button .close-all')) {
            const closeButton = event.target;
            closeButton.classList.remove('active');
            const expandButton = closeButton.parentNode.querySelector('.expand-all');
            expandButton.classList.add('active');

            const tabsComp = closeButton.closest('.cmp-tabs');
            const accButtons = tabsComp.querySelectorAll('.acc-button');
            const tabpanels = tabsComp.querySelectorAll('.cmp-tabs__tabpanel');

            accButtons.forEach(btn => {
                btn.classList.remove('active');
                btn.classList.add('closed');
            });

            tabpanels.forEach(panel => {
                slideUp(panel, 500, function() {
                    const tabRef = this.id;
                    const correspondingTab = document.querySelector(`#${tabRef}`);
                    const correspondingTabBtn = document.querySelector(`#${tabRef}-tab`);

                    if (correspondingTab) {
                        correspondingTab.classList.remove('cmp-tabs__tabpanel--active');
                        correspondingTab.setAttribute('aria-hidden', 'true');
                        correspondingTab.removeAttribute('style');
                    }

                    if (correspondingTabBtn) {
                        correspondingTabBtn.classList.remove('cmp-tabs__tab--active');
                        correspondingTabBtn.setAttribute('aria-selected', 'false');
                        correspondingTabBtn.setAttribute('tabindex', '-1');
                    }
                });
            });
        }
    });

    /* MOBILE (ACCORDION) - TOGGLE ITEM */
    document.addEventListener('click', function(event) {
        if (event.target.matches('.acc-button')) {
            const accButton = event.target;
            const tabPanel = accButton.nextElementSibling;
            const tabRef = tabPanel.id;
            const correspondingTab = document.querySelector(`#${tabRef}`);
            const correspondingTabBtn = document.querySelector(`#${tabRef}-tab`);

            if (accButton.classList.contains('active')) {
                accButton.classList.remove('active');
                accButton.classList.add('closed');

                slideUp(tabPanel, 500, function() {
                    if (correspondingTab) {
                        correspondingTab.classList.remove('cmp-tabs__tabpanel--active');
                        correspondingTab.setAttribute('aria-hidden', 'true');
                        correspondingTab.removeAttribute('style');
                    }

                    if (correspondingTabBtn) {
                        correspondingTabBtn.classList.remove('cmp-tabs__tab--active');
                        correspondingTabBtn.setAttribute('aria-selected', 'false');
                        correspondingTabBtn.setAttribute('tabindex', '-1');
                    }
                });
            } else {
                accButton.classList.remove('closed');
                accButton.classList.add('active');

                slideDown(tabPanel, 500, function() {
                    if (correspondingTab) {
                        correspondingTab.classList.add('cmp-tabs__tabpanel--active');
                        correspondingTab.removeAttribute('aria-hidden');
                        correspondingTab.removeAttribute('style');
                    }

                    if (correspondingTabBtn) {
                        correspondingTabBtn.classList.add('cmp-tabs__tab--active');
                        correspondingTabBtn.setAttribute('aria-selected', 'true');
                        correspondingTabBtn.setAttribute('tabindex', '0');
                    }
                });

                const carouselInTab = tabPanel.querySelector('.carousel');
                if (carouselInTab) {
                    window.dispatchEvent(new CustomEvent('resize-carousel'));
                }
            }

            const tabsComp = accButton.closest('.cmp-tabs');
            const closedTabs = tabsComp.querySelectorAll('.acc-button.closed');
            const expandButton = tabsComp.querySelector('.expand-all');
            const closeButton = tabsComp.querySelector('.close-all');

            if (closedTabs.length === 0) {
                expandButton.classList.remove('active');
                closeButton.classList.add('active');
            } else {
                expandButton.classList.add('active');
                closeButton.classList.remove('active');
            }
        }
    });

    /* MOBILE (ACCORDION) - MAINTAIN ACTIVE TAB ON RESIZE TO MOBILE (OR FIRST ACTIVE TAB ON RESIZE TO DESKTOP) */
    window.addEventListener('resize', function() {
        if (window.innerWidth > 1023) {
            const tabComponents = document.querySelectorAll('.cmp-tabs');
            tabComponents.forEach(function(tabComponent) {
                const openTabs = tabComponent.querySelectorAll('.cmp-tabs__tab--active');
                if (openTabs.length === 0) { // if all closed, open first tab
                    const firstAccButton = tabComponent.querySelector('.acc-button');
                    const firstTab = tabComponent.querySelector('.cmp-tabs__tab');
                    const firstTabPanel = tabComponent.querySelector('.cmp-tabs__tabpanel');

                    if (firstAccButton) {
                        firstAccButton.classList.add('active');
                        firstAccButton.classList.remove('closed');
                    }

                    if (firstTab) {
                        firstTab.classList.add('cmp-tabs__tab--active');
                        firstTab.setAttribute('aria-selected', 'true');
                        firstTab.setAttribute('tabindex', '0');
                    }

                    if (firstTabPanel) {
                        slideDown(firstTabPanel, 500, function() {
                            this.classList.add('cmp-tabs__tabpanel--active');
                            this.setAttribute('aria-hidden', 'false');
                            this.removeAttribute('style');
                        });
                    }
                } else { // close all except first open tab
                    const activeAccButtons = tabComponent.querySelectorAll('.acc-button.active');
                    const activeTabs = tabComponent.querySelectorAll('.cmp-tabs__tab--active');
                    const activeTabPanels = tabComponent.querySelectorAll('.cmp-tabs__tabpanel--active');

                    // Close all except first
                    for (let i = 1; i < activeAccButtons.length; i++) {
                        activeAccButtons[i].classList.remove('active');
                        activeAccButtons[i].classList.add('closed');
                    }

                    for (let i = 1; i < activeTabs.length; i++) {
                        activeTabs[i].classList.remove('cmp-tabs__tab--active');
                        activeTabs[i].setAttribute('aria-selected', 'false');
                        activeTabs[i].setAttribute('tabindex', '-1');
                    }

                    for (let i = 1; i < activeTabPanels.length; i++) {
                        slideUp(activeTabPanels[i], 500, function() {
                            this.classList.remove('cmp-tabs__tabpanel--active');
                            this.setAttribute('aria-hidden', 'true');
                            this.removeAttribute('style');
                        });
                    }
                }
            });
        }
    });

    // Trigger resize on load
    window.addEventListener('load', function() {
        window.dispatchEvent(new Event('resize'));
    });

}());
